import type React from 'react';
import type { GlossaryEntry, CascadeStep, CascadeArticle, GlossaryRef } from '@/types';
import citedNormsData from '@/data/cited_norms.json';
import { glossaryHashFor } from '@/hooks/useHashRoute';
import styles from './AppHeader.module.css';

const CITED_NORMS = citedNormsData as Record<string, { code: string; shortName: string; fullName: string; url: string }>;

// Cascade depth → tint laterale (sottile ma colorata, R-M3: max depth 5)
const CASCADE_TINT: Record<number, string> = {
  2: '#7BA0C4',  // grigio-blu polvere
  3: '#9384B8',  // grigio-viola polvere
  4: '#B58BA8',  // grigio-magenta polvere
  5: '#C49889',  // grigio-terracotta polvere
};

const ACT_LABEL: Record<string, string> = { amld: 'AMLD', amlr: 'AMLR', amlar: 'AMLAR', tfr: 'TFR' };
const ACT_COLOR: Record<string, string> = { amld: '#1D4ED8', amlr: '#0D9488', amlar: '#7C3AED', tfr: '#C2410C' };

// Stats di espansione per il header informativo (#2 — "effort di sviluppo deve risultare")
export function expansionStats(entries: GlossaryEntry[]) {
  let entriesWithExp = 0;
  let totalNotes = 0;
  let totalVerbatim = 0;
  let maxDepthSeen = 0;
  for (const e of entries) {
    if (!e.expansions || e.expansions.length === 0) continue;
    entriesWithExp += 1;
    for (const exp of e.expansions) {
      totalNotes += 1;
      const walk = (steps: CascadeStep[]) => {
        for (const s of steps) {
          if (s.depth > maxDepthSeen) maxDepthSeen = s.depth;
          for (const a of s.articles) {
            if (a.verbatim && a.provenance?.approvedBy === 'DB_owner') totalVerbatim += 1;
          }
          if (s.children) walk(s.children);
        }
      };
      walk(exp.cascade);
    }
  }
  return { entriesWithExp, totalNotes, totalVerbatim, maxDepthSeen };
}

// Splits the gloss text around the anchor phrase (first match, case-sensitive — R-M5).
function splitGlossAroundAnchor(gloss: string, anchor: string): { before: string; anchor: string; after: string } {
  const i = gloss.indexOf(anchor);
  if (i < 0) return { before: gloss, anchor: '', after: '' };
  return {
    before: gloss.slice(0, i),
    anchor: gloss.slice(i, i + anchor.length),
    after: gloss.slice(i + anchor.length),
  };
}

function citesText(article: CascadeArticle): string {
  if (!article.citesTerms || article.citesTerms.length === 0) return '';
  return article.citesTerms.join(', ');
}

// Renders a text fragment with J cross-term links applied. Each glossaryRef.phrase
// is replaced in-place (case-insensitive first match) with a link to #/glossary/<targetEntryId>.
// Multiple non-overlapping matches across different phrases are supported.
// Anti-allucinazione: only phrases listed in glossaryRefs are linked — no pattern matching.
function linkifyCrossTerms(
  text: string,
  refs: GlossaryRef[] | undefined,
  scope: 'gloss' | 'officialText',
): React.ReactNode {
  if (!refs || refs.length === 0) return text;

  // Collect all match positions for refs that target this scope.
  type Match = { start: number; end: number; targetEntryId: string; matched: string };
  const matches: Match[] = [];
  for (const ref of refs) {
    const scopes: ('gloss' | 'officialText')[] = ref.scope && ref.scope.length > 0 ? ref.scope : ['gloss'];
    if (!scopes.includes(scope)) continue;
    // case-insensitive search
    const lower = text.toLowerCase();
    const needle = ref.phrase.toLowerCase();
    let from = 0;
    while (true) {
      const i = lower.indexOf(needle, from);
      if (i < 0) break;
      // Skip if overlapping with an existing match (first ref wins).
      const overlaps = matches.some((m) => i < m.end && i + needle.length > m.start);
      if (!overlaps) {
        matches.push({
          start: i,
          end: i + needle.length,
          targetEntryId: ref.targetEntryId,
          matched: text.slice(i, i + needle.length), // preserve original casing
        });
      }
      from = i + needle.length;
    }
  }
  if (matches.length === 0) return text;

  // Sort matches by start position and stitch the output.
  matches.sort((a, b) => a.start - b.start);
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  matches.forEach((m, idx) => {
    if (m.start > cursor) parts.push(text.slice(cursor, m.start));
    parts.push(
      <a
        key={`x-${idx}`}
        className={styles.xterm}
        href={glossaryHashFor(m.targetEntryId)}
        title={`See glossary entry: ${m.targetEntryId}`}
        // J opens in same tab inside the modal context (handled by deep-link routing),
        // and in a new tab when used from the standalone reader (target="_blank").
        // Behaviour follows the user's browser default for internal anchors.
      >
        {m.matched}
      </a>
    );
    cursor = m.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

// Pretty-prints the source article for the "no verbatim" hint. AMLD/AMLR/AMLAR use Art. 2;
// TFR uses Art. 3. Avoids the over-assertive "no formal Art.2/3 definition" phrasing —
// stays open to the possibility a definition exists in another (e.g. international) source.
function noVerbatimNote(actId: string | undefined): string {
  const art = actId === 'tfr' ? 'Art. 3' : 'Art. 2';
  const label =
    actId === 'amld' ? 'AMLD' :
    actId === 'amlr' ? 'AMLR' :
    actId === 'amlar' ? 'AMLAR' :
    actId === 'tfr' ? 'TFR' :
    'the package';
  return `Definition not included in ${label} ${art}.`;
}

// CascadeStepView — renders a single step of the cascade tree (recursive).
function CascadeStepView({
  step,
  entryId,
  defaultOpen,
  isStepExpanded,
  toggleStep,
}: {
  step: CascadeStep;
  entryId: string;
  defaultOpen: boolean;
  isStepExpanded: (entryId: string, label: string) => boolean;
  toggleStep: (entryId: string, label: string) => void;
}) {
  const userToggled = isStepExpanded(entryId, step.label);
  const open = defaultOpen ? !userToggled : userToggled;
  const tint = CASCADE_TINT[step.depth] || '#CBD5E1';

  if (step.truncated) {
    return (
      <div className={styles.lv} style={{ borderLeftColor: '#E8DAB8' }}>
        <div className={styles.lvHead}>
          <span className={styles.lvNum}>{step.label}</span>
          <span className={`${styles.lvSrc} ${styles.lvSrcQuiet}`}>nesting limit reached</span>
        </div>
        <div className={styles.lvTrunc}>
          {step.truncated.reason === 'max_depth_5' && step.truncated.rejoinTerm && step.truncated.rejoinEntryId ? (
            <>
              The term <em>“{step.truncated.rejoinTerm}”</em> referenced above has its own glossary entry with further nested references.
              <br />
              <a
                className={styles.lvTruncLink}
                href={glossaryHashFor(step.truncated.rejoinEntryId)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open “{step.truncated.rejoinTerm}” in a new tab →
              </a>
            </>
          ) : step.truncated.reason === 'fetch_failed' ? (
            <>Verbatim retrieval pending. The cited text will be added by the database owner once verified against the official source.</>
          ) : (
            <>The reference chain forms a cycle and stops here.</>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.lv} style={{ borderLeftColor: tint }}>
      <div className={styles.lvHead}>
        <span className={styles.lvNum}>{step.label}</span>
        <span className={styles.lvSrc}>
          {step.articles[0] ? `${step.norm.code} · ${step.articles[0].ref}` : step.norm.code}
          {step.articles[0]?.topic ? ` · ${step.articles[0].topic}` : ''}
        </span>
        <span
          className={styles.pill}
          data-status={step.norm.status === 'repealed' ? 'repealed' : 'in-force'}
        >
          {step.norm.status === 'repealed' ? 'repealed' : 'in force'}
        </span>
        <button
          type="button"
          className={styles.lvOpen}
          onClick={() => toggleStep(entryId, step.label)}
          aria-label={open ? `Collapse ${step.label}` : `Expand ${step.label}`}
        >
          {open ? '−' : '+'}
        </button>
      </div>
      {open && step.articles.map((art, i) => (
        <div key={i}>
          {art.verbatim ? (
            <div className={styles.lvText}>{art.verbatim}</div>
          ) : (
            <div className={`${styles.lvText} ${styles.lvTextQuiet}`}>
              Verbatim pending verification by database owner.
            </div>
          )}
          {citesText(art) && (
            <div className={styles.lvCites}>
              cites: {art.citesTerms!.map((t, j) => (
                <span key={j}>
                  <em>{t}</em>{j < art.citesTerms!.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
      {!open && step.articles[0]?.verbatim && (
        <div className={`${styles.lvText} ${styles.lvTextQuiet} ${styles.lvTextClamp}`}>
          {step.articles[0].verbatim}
        </div>
      )}
      {open && step.children && step.children.map((child) => (
        <CascadeStepView
          key={child.label}
          step={child}
          entryId={entryId}
          defaultOpen={false}
          isStepExpanded={isStepExpanded}
          toggleStep={toggleStep}
        />
      ))}
    </div>
  );
}

// ─── GlossaryEntryView — shared between modal and full-page reader ───
export function GlossaryEntryView({
  entry: e,
  isStepExpanded,
  toggleStep,
  entryRef,
  focused,
}: {
  entry: GlossaryEntry;
  isStepExpanded: (entryId: string, label: string) => boolean;
  toggleStep: (entryId: string, label: string) => void;
  entryRef?: (el: HTMLDivElement | null) => void;
  focused?: boolean;
}) {
  const act = e.source?.act;
  const color = ACT_COLOR[act] || '#475569';
  const isEba = e.verified === 'amla_draft';
  const srcLabel = isEba
    ? (e.source?.guideline || 'EBA')
    : (ACT_LABEL[act] || '') + (e.source?.article ? ` Art. ${String(e.source.article).replace('art_', '')}` : '');
  const expansion = e.expansions && e.expansions.length > 0 ? e.expansions[0] : null;
  const split = expansion ? splitGlossAroundAnchor(e.gloss, expansion.anchorPhrase) : null;

  return (
    <div
      ref={entryRef}
      className={`${styles.glossEntry} ${focused ? styles.glossEntryFocused : ''}`}
      style={{ borderLeftColor: color }}
    >
      <div className={styles.glossEntryTop} data-tour="gl-entry-top">
        <span className={styles.glossTerm}>{e.term}</span>
        {e.acronym && <span className={styles.glossAcr}>{e.acronym}</span>}
        {e.source?.eli && (
          <a className={styles.glossSrc} style={{ color }} href={e.source.eli} target="_blank" rel="noopener noreferrer">
            {srcLabel} <span aria-hidden="true">↗</span>
          </a>
        )}
      </div>
      <div className={styles.glossGloss} data-tour="gl-gloss">
        <span className={styles.glossAiLabel}>Plain-language gloss · AI</span>
        {split ? (
          <>
            {linkifyCrossTerms(split.before, e.glossaryRefs, 'gloss')}
            {split.anchor && (
              <span className={styles.glossAnchor}>
                {split.anchor}
                <sup className={styles.glossFnMark}>1</sup>
              </span>
            )}
            {linkifyCrossTerms(split.after, e.glossaryRefs, 'gloss')}
          </>
        ) : linkifyCrossTerms(e.gloss, e.glossaryRefs, 'gloss')}
      </div>
      {expansion && (
        <div className={styles.glossFootnotes} data-tour="gl-cascade">
          <div className={styles.glossFnIntro}>
            <span className={styles.glossFnNumCircle}>1</span>
            <span>
              The highlighted terms are themselves defined elsewhere. Below: the
              referenced definitions and — in turn — the further terms they cite,
              up to {expansion.maxDepth} levels of nesting.
            </span>
          </div>
          <div className={styles.glossCascade}>
            {expansion.cascade.map((step) => (
              <CascadeStepView
                key={step.label}
                step={step}
                entryId={e.id}
                defaultOpen={step.label === expansion.cascade[0].label}
                isStepExpanded={isStepExpanded}
                toggleStep={toggleStep}
              />
            ))}
          </div>
        </div>
      )}
      {/* Evolutiva K — Established by (organisation-acronyms) */}
      {e.establishedBy && CITED_NORMS[e.establishedBy.normKey] && (
        <div className={styles.establishedBy} data-tour="gl-established">
          <span className={styles.establishedByLabel}>Established by</span>
          <a
            href={CITED_NORMS[e.establishedBy.normKey].url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {CITED_NORMS[e.establishedBy.normKey].code}
            {e.establishedBy.article ? `, ${e.establishedBy.article}` : ''}
          </a>
          {e.establishedBy.description && (
            <span className={styles.establishedByDesc}> — {e.establishedBy.description}</span>
          )}
        </div>
      )}
      {e.officialText ? (
        <details className={styles.glossOfficial} data-tour="gl-verbatim">
          <summary>Verbatim definition · {isEba ? 'EBA' : 'EUR-Lex'}</summary>
          <div className={styles.glossVerbatim}>
            <span className={styles.glossVtag}>✓ Verbatim · {isEba ? 'EBA' : 'EUR-Lex'}</span><br />
            {linkifyCrossTerms(e.officialText, e.glossaryRefs, 'officialText')}
          </div>
        </details>
      ) : (
        <div className={styles.glossAcrNote}>{noVerbatimNote(act)}</div>
      )}
    </div>
  );
}
